import React, { useMemo, useState } from "react";
import { Check, Plus, Truck, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { fmtNOK } from "@/lib/format";
import { PRICE_TYPE_LABEL } from "@/lib/sk";
import { BLANK } from "@/lib/veiviser";
import { fieldCls } from "@/components/veiviser/WizardChrome";

function Row({ r, on, onToggle }) {
  return (
    <li>
      <button type="button" onClick={onToggle} aria-pressed={on} className={cn("group flex w-full items-start gap-3.5 rounded-[12px] px-3 py-3 text-left outline-none transition-colors hover:bg-black/[0.025] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30", !on && "opacity-55")} data-testid={`vv-post-${r.ref}`} data-on={on ? "1" : "0"}>
        <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border transition-colors", on ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy))] text-white" : "border-black/[0.2] bg-white text-transparent")}><Check className="h-3 w-3" strokeWidth={3.2} /></span>
        <span className="w-12 shrink-0 pt-px font-mono text-[12.5px] tabular-nums text-muted-foreground">{r.ref}</span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={cn("text-[14.5px] font-medium text-foreground", !on && "line-through decoration-black/25")}>{r.title}</span>
            {r.price_type === "opsjon" ? <span className="v-badge v-assumed">Opsjon</span> : null}
            {r.supplier ? <span className="v-badge v-price" title="Underleverandør – veiledende pris"><Truck className="h-3 w-3" /> UL</span> : null}
          </span>
          <span className="mt-0.5 line-clamp-2 text-[12.5px] leading-relaxed text-muted-foreground">{r.text}</span>
        </span>
        <span className="shrink-0 pt-px text-right">
          <span className="block font-mono text-[13px] tabular-nums text-foreground">{r.in_sum || r.price_type === "opsjon" ? `${fmtNOK(r.price)} kr` : PRICE_TYPE_LABEL[r.price_type] || "–"}</span>
          <span className="block text-[11.5px] text-muted-foreground">{PRICE_TYPE_LABEL[r.price_type] || r.price_type}{r.hours ? ` · ${fmtNOK(r.hours)} t` : ""}</span>
        </span>
      </button>
    </li>
  );
}

function OwnPosts({ v, setV }) {
  const [t, setT] = useState("");
  const add = () => { const s = t.trim(); if (!s) return; setV((cur) => ({ ...cur, extra: [...cur.extra, s] })); setT(""); };
  return (
    <section className="mt-8" data-testid="vv-extra">
      <div className="text-[13px] font-semibold text-foreground">Egne poster</div>
      <p className="mt-0.5 text-[12.5px] text-muted-foreground">Det kunden har bedt om som ikke står i malen. Postene legges inn uten pris – du kalkulerer dem selv.</p>
      <div className="mt-3 flex gap-2">
        <input value={t} onChange={(e) => setT(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); add(); } }} placeholder="F.eks. Bytte lanternesentral til LED" className={fieldCls} data-testid="vv-extra-input" />
        <button type="button" onClick={add} disabled={!t.trim()} className="inline-flex h-12 shrink-0 items-center gap-1.5 rounded-[12px] border border-black/[0.1] bg-white px-4 text-[13.5px] font-semibold text-foreground transition-colors hover:border-black/[0.25] disabled:opacity-40" data-testid="vv-extra-add"><Plus className="h-4 w-4" /> Legg til</button>
      </div>
      {v.extra.length ? (
        <ul className="mt-3 divide-y divide-black/[0.05] rounded-[14px] border border-black/[0.06]">
          {v.extra.map((x, i) => (
            <li key={`${x}-${i}`} className="flex items-center gap-3 px-3.5 py-2.5 text-[14px]" data-testid={`vv-extra-${i}`}>
              <span className="w-10 font-mono text-[12px] text-muted-foreground">M{i + 1}</span><span className="min-w-0 flex-1 truncate">{x}</span>
              <button type="button" onClick={() => setV((cur) => ({ ...cur, extra: cur.extra.filter((_, j) => j !== i) }))} className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" aria-label="Fjern" data-testid={`vv-extra-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function StepOmfang({ v, setV, preview, previewing }) {
  const groups = useMemo(() => {
    const out = [];
    (preview?.items || []).forEach((r) => { const g = out.find((x) => x.label === r.group); if (g) g.rows.push(r); else out.push({ label: r.group, rows: [r] }); });
    return out;
  }, [preview]);
  const toggle = (ref) => setV((cur) => ({ ...cur, off: cur.off.includes(ref) ? cur.off.filter((x) => x !== ref) : [...cur.off, ref] }));
  const setAll = (on) => setV((cur) => ({ ...cur, off: on ? [] : (preview?.items || []).map((i) => i.ref) }));
  if (v.template === BLANK) return <div data-testid="vv-omfang"><p className="text-[14px] text-muted-foreground">Tomt oppdrag: ingen standardposter. Legg inn det du vet nå – resten kan komme fra kundens dokumenter etterpå.</p><OwnPosts v={v} setV={setV} /></div>;
  if (!preview) return <div className="space-y-2" data-testid="vv-omfang-loading">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-[12px] bg-black/[0.04]" />)}</div>;
  const on = preview.items.filter((i) => !v.off.includes(i.ref)).length;
  return (
    <div data-testid="vv-omfang" data-loading={previewing ? "1" : "0"} className={cn("transition-opacity", previewing && "opacity-60")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-muted-foreground" data-testid="vv-omfang-count"><span className="font-semibold text-foreground">{on} av {preview.items.length}</span> poster fra «{preview.name}»</p>
        <div className="flex gap-1">
          <button type="button" onClick={() => setAll(true)} className="h-8 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid="vv-omfang-all">Velg alle</button>
          <button type="button" onClick={() => setAll(false)} className="h-8 rounded-full px-3 text-[12.5px] font-medium text-muted-foreground hover:bg-black/[0.05] hover:text-foreground" data-testid="vv-omfang-none">Velg bort alle</button>
        </div>
      </div>
      <div className="mt-3 space-y-5">
        {groups.map((g) => (
          <section key={g.label}>
            <div className="px-3 pb-1 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground/80">{g.label}</div>
            <ul className="divide-y divide-black/[0.04]">{g.rows.map((r) => <Row key={r.ref} r={r} on={!v.off.includes(r.ref)} onToggle={() => toggle(r.ref)} />)}</ul>
          </section>
        ))}
      </div>
      <p className="mt-4 rounded-[12px] bg-[hsl(var(--brand-soft))] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-[hsl(208_90%_30%)]" data-testid="vv-omfang-basis">{preview.basis} Prisene er forslag og står som «ikke vurdert» til du har gått gjennom dem.</p>
      <OwnPosts v={v} setV={setV} />
    </div>
  );
}
